import { create } from 'zustand';

import { DocumentVerificationEnum, TransactionProcess } from '../enums';

type TransactionItem = {
  transactionType: string;
  currency: string;
  amount: number;
  exchangeRate: number;
  exchangeTime: string;
  isSupportedByOmFx: boolean;
};

type InitialState = {
  refId: string;
  userId: string;
  transactionType: string;
  isTransactionProcessAtBranch: boolean;
  isTransactionProcessOnline: boolean;
  isDocumentVerificationAtBranch: boolean;
  isDocumentVerificationOnline: boolean;
  couponCode: string;
  couponType: string;
  discount: number;
  orderAndCurrencyDetails: {
    cardNumber?: string;
    transactionProcess: string;
    transactionItems: TransactionItem[];
    amountInfo: {
      payableAmount: number;
      purchaseAmount: number;
      serviceCharge: number;
      gst: number;
      tcs: number;
    };
  };
  documentVerification: {
    verifyAt: DocumentVerificationEnum.Branch | DocumentVerificationEnum.Online;
    pincode: string;
    appointment: {
      branchId: string;
      date: string;
      timeslot: string;
      branchLatitude: string;
      branchLongitude: string;
      duration: string;
      branchLocation: string;
      branchContact: string;
      branchAddress: string;
      tripType?: 'NEW' | 'EXISTING';
      parentTripId?: string;
    };
  };
  uploadDocument: {
    purpose: string;
    relationship: string;
    senderDocs: string[];
    receiverDocs: string[];
    senderDocsUploaded: string[];
    receiverDocsUploaded: string[];
    isDocumentDeclarationAccepted: boolean;
    isLrsAndTcsDeclarationAccepted: boolean;
  };
  userInfo: {
    name: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    pincode: string;
    travellingFor: string;
    travellingTo: string;
    startDate: string;
    endDate: string;
    receiverName: string;
    receiverMobileNo: string;
    receiverBankName: string;
    receiverBranchName: string;
    receiverAccountNo: string;
    swiftCode: string;
    accountHolderName: string;
    accountNumber: string;
    bankName: string;
    ifscCode: string;
    branchName: string;
  };
  orderInfo: {
    orderId: string;
    orderDateTime: string;
    orderStatus: string;
  };
  sourceInfo: {
    source: 'DIRECT' | 'BRANCH_ASSISTED';
    medium: 'Web';
    product: 'FX';
    branch: string;
    branchEmployeeCode: string;
    refId: string;
  };
};

export const initialState: InitialState = {
  refId: '',
  userId: '',
  transactionType: '',
  orderAndCurrencyDetails: {
    cardNumber: '',
    transactionProcess: TransactionProcess.Online,
    transactionItems: [],
    amountInfo: {
      payableAmount: 0,
      purchaseAmount: 0,
      serviceCharge: 0,
      gst: 0,
      tcs: 0,
    },
  },
  documentVerification: {
    verifyAt: DocumentVerificationEnum.Branch,
    pincode: '',
    appointment: {
      branchId: '',
      date: '',
      timeslot: '',
      branchAddress: '',
      branchLatitude: '',
      branchLongitude: '',
      duration: '',
      branchLocation: '',
      branchContact: '',
      tripType: 'NEW',
      parentTripId: '',
    },
  },
  uploadDocument: {
    purpose: '',
    relationship: '',
    senderDocs: [],
    receiverDocs: [],
    senderDocsUploaded: [],
    receiverDocsUploaded: [],
    isDocumentDeclarationAccepted: false,
    isLrsAndTcsDeclarationAccepted: false,
  },
  userInfo: {
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    travellingFor: '',
    travellingTo: '',
    startDate: '',
    endDate: '',
    receiverName: '',
    receiverMobileNo: '',
    receiverBankName: '',
    receiverBranchName: '',
    receiverAccountNo: '',
    swiftCode: '',
    accountHolderName: '',
    accountNumber: '',
    bankName: '',
    ifscCode: '',
    branchName: '',
    // accountHolderName: 'chillapalli teja venkata sabarinath',
    // accountNumber: '62432406226',
    // bankName: 'state bank of india',
    // ifscCode: 'SBIN0021233',
    // branchName: 'rgu iiit nuzvid',
  },
  orderInfo: {
    orderId: '',
    orderDateTime: '',
    orderStatus: '',
  },
  sourceInfo: {
    source: 'DIRECT',
    medium: 'Web',
    product: 'FX',
    branch: '',
    branchEmployeeCode: '',
    refId: '',
  },
  isTransactionProcessAtBranch: false,
  isTransactionProcessOnline: true,
  isDocumentVerificationAtBranch: true,
  isDocumentVerificationOnline: false,
  couponCode: '',
  couponType: '',
  discount: 0,
};

export type StateMethods = {
  updateOrderAndCurrencyDetails: (
    orderAndCurrencyDetails: Partial<InitialState['orderAndCurrencyDetails']>
  ) => void;
  updateDocumentVerification: (
    documentVerificationDetails: Partial<InitialState['documentVerification']>
  ) => void;
  updateUploadDocument: (
    uploadDocumentDetails: Partial<InitialState['uploadDocument']>
  ) => void;
  updateUserInfo: (updateUserInfo: Partial<InitialState['userInfo']>) => void;
  updateOrderInfo: {
    (orderInfo: Partial<InitialState['orderInfo']>): void;
  };
  updateSourceInfo: (
    updateSourceInfo: Partial<InitialState['sourceInfo']>
  ) => void;
  updateState: (data: Partial<InitialState>) => void;
  resetState: () => void;
};

export const useForexStore = create<InitialState & StateMethods>((set) => ({
  ...initialState,
  updateOrderAndCurrencyDetails: (orderAndCurrencyDetails) =>
    set((state) => ({
      ...state,
      orderAndCurrencyDetails: {
        ...state.orderAndCurrencyDetails,
        ...orderAndCurrencyDetails,
      },
    })),
  updateDocumentVerification: (documentVerificationDetails) =>
    set((state) => ({
      ...state,
      documentVerification: {
        ...state.documentVerification,
        ...documentVerificationDetails,
      },
    })),
  updateUploadDocument: (uploadDocumentDetails) =>
    set((state) => ({
      ...state,
      uploadDocument: {
        ...state.uploadDocument,
        ...uploadDocumentDetails,
      },
    })),
  updateUserInfo: (updateUserInfo) =>
    set((state) => ({
      ...state,
      userInfo: {
        ...state.userInfo,
        ...updateUserInfo,
      },
    })),
  updateOrderInfo: (orderInfo) =>
    set((state) => ({
      ...state,
      orderInfo: {
        ...state.orderInfo,
        ...orderInfo,
      },
    })),
  updateSourceInfo: (sourceInfo) =>
    set((state) => ({
      ...state,
      sourceInfo: {
        ...state.sourceInfo,
        ...sourceInfo,
      },
    })),
  updateState: (data) =>
    set((state) => ({
      ...state,
      ...data,
    })),
  resetState: () => set(initialState),
}));
